import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangeCalendar } from '@mui/x-date-pickers-pro/DateRangeCalendar';

import { Dayjs } from 'dayjs';

import AppButton from '@/shared/ui/AppButton';
import { UnavailableLegend } from '@/shared/ui/CalendarDatePicker';

import CalendarDateRangePickerDay from './CalendarDateRangePickerDay';
import CalendarDateRangePickerHeader from './CalendarDateRangePickerHeader';
import { getRestartingRangeChange } from './dateRangeChange.utils';
import { getDayCalendarSx } from './dayCalendarGridSx';

const CALENDAR_COLUMN_GAP = '32px';

export interface CalendarDateRangePickerProps {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onChange: (startDate: Dayjs | null, endDate: Dayjs | null) => void;
  /**
   * When provided, renders an "Apply" button next to Clear — for callers that
   * need an explicit commit step instead of applying on every pick (the
   * default, used when this prop is omitted).
   */
  onApply?: (startDate: Dayjs | null, endDate: Dayjs | null) => void;
  /** Extra action rendered alongside Clear/Apply (e.g. a "Delete Booking" button). */
  extraAction?: ReactNode;
  /** Hides the Clear button for callers whose range must always hold a value. */
  hideClear?: boolean;
  isDateUnavailable?: (date: Dayjs, position?: 'start' | 'end') => boolean;
  isDateIndividuallyUnavailable?: (date: Dayjs) => boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disablePast?: boolean;
  showDateInputs?: boolean;
  restartOnCompleteRangeClick?: boolean;
  calendars?: 1 | 2;
  hideUnavailableLegend?: boolean;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
}

const CalendarIcon: FC<{ fill: string }> = ({ fill }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.6667 0.833496C10.9428 0.833496 11.1667 1.05735 11.1667 1.3335V2.16683H12.6667C13.6792 2.16683 14.5 2.98764 14.5 4.00016V13.3335C14.5 14.346 13.6792 15.1668 12.6667 15.1668H3.33333C2.32081 15.1668 1.5 14.346 1.5 13.3335V4.00016C1.5 2.98764 2.32081 2.16683 3.33333 2.16683H4.83333V1.3335C4.83333 1.05735 5.05719 0.833496 5.33333 0.833496C5.60948 0.833496 5.83333 1.05735 5.83333 1.3335V2.16683H10.1667V1.3335C10.1667 1.05735 10.3905 0.833496 10.6667 0.833496ZM2.5 7.16683V13.3335C2.5 13.7937 2.8731 14.1668 3.33333 14.1668H12.6667C13.1269 14.1668 13.5 13.7937 13.5 13.3335V7.16683H2.5ZM3.33333 3.16683C2.8731 3.16683 2.5 3.53993 2.5 4.00016V6.16683H13.5V4.00016C13.5 3.53993 13.1269 3.16683 12.6667 3.16683H11.1667V4.00016C11.1667 4.2763 10.9428 4.50016 10.6667 4.50016C10.3905 4.50016 10.1667 4.2763 10.1667 4.00016V3.16683H5.83333V4.00016C5.83333 4.2763 5.60948 4.50016 5.33333 4.50016C5.05719 4.50016 4.83333 4.2763 4.83333 4.00016V3.16683H3.33333Z"
      fill={fill}
    />
  </svg>
);

const ArrowRightIcon: FC<{ fill: string }> = ({ fill }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M7.64714 2.97994C7.8424 2.78468 8.1589 2.78468 8.35417 2.97994L13.0208 7.64661C13.0531 7.67888 13.078 7.71559 13.0996 7.75338C13.11 7.77148 13.1208 7.78929 13.1289 7.80872C13.1427 7.84205 13.1526 7.87652 13.1589 7.91158C13.164 7.94035 13.1673 7.96988 13.1673 8.00012C13.1673 8.02994 13.1639 8.05899 13.1589 8.08736C13.1526 8.12266 13.1428 8.15733 13.1289 8.19088C13.115 8.22446 13.0974 8.25586 13.0768 8.28528C13.0602 8.30909 13.0421 8.33239 13.0208 8.35364L8.35417 13.0203C8.15892 13.2155 7.8424 13.2155 7.64714 13.0203C7.45188 12.8251 7.45189 12.5085 7.64714 12.3133L11.4603 8.50013H3.33398C3.05785 8.50013 2.834 8.27625 2.83398 8.00012C2.83398 7.72398 3.05784 7.50012 3.33398 7.50012H11.4603L7.64714 3.68697C7.45188 3.49172 7.45189 3.17521 7.64714 2.97994Z"
      fill={fill}
    />
  </svg>
);

const RangeDateInput: FC<{ label: string; value: Dayjs | null }> = ({ label, value }) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="8px"
      aria-label={label}
      sx={{
        flex: 1,
        height: '40px',
        border: '1px solid rgba(0, 0, 0, 0.15)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '12px 16px',
      }}
    >
      <CalendarIcon fill={theme.palette.grey[400]} />
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: 1.3,
          color: (theme) => (value ? theme.palette.grey[900] : theme.palette.grey[400]),
        }}
      >
        {value ? value.format('MMM D, YYYY') : label}
      </Typography>
    </Stack>
  );
};

const CalendarDateRangePicker: FC<CalendarDateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  onApply,
  extraAction,
  hideClear,
  isDateUnavailable,
  isDateIndividuallyUnavailable = isDateUnavailable,
  minDate,
  maxDate,
  disablePast,
  showDateInputs = true,
  restartOnCompleteRangeClick = false,
  calendars = 2,
  hideUnavailableLegend,
  sx,
  'data-testid': dataTestId = 'calendar-date-range-picker',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      data-testid={dataTestId}
      sx={[
        {
          backgroundColor: '#FEFEFE',
          boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.15)',
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: 'fit-content',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {showDateInputs && (
        <Stack direction="row" alignItems="center" gap={0}>
          <RangeDateInput label={t('starting_date')} value={startDate} />
          <Box
            sx={{
              width: CALENDAR_COLUMN_GAP,
              flexShrink: 0,
              display: 'flex',
              height: '40px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArrowRightIcon fill={theme.palette.grey[400]} />
          </Box>
          <RangeDateInput label={t('ending_date')} value={endDate} />
        </Stack>
      )}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateRangeCalendar
          value={[startDate, endDate]}
          onChange={(newRange) => {
            const [nextStart, nextEnd] = restartOnCompleteRangeClick
              ? getRestartingRangeChange([startDate, endDate], newRange)
              : newRange;
            onChange(nextStart, nextEnd);
          }}
          calendars={calendars}
          minDate={minDate}
          maxDate={maxDate}
          disablePast={disablePast}
          reduceAnimations
          dayOfWeekFormatter={(date) => date.format('dd')}
          shouldDisableDate={isDateUnavailable}
          slots={{
            calendarHeader: CalendarDateRangePickerHeader,
            day: (dayProps) => (
              <CalendarDateRangePickerDay
                {...dayProps}
                isDateUnavailable={isDateIndividuallyUnavailable}
                hasCompleteRange={!!(startDate && endDate)}
              />
            ),
          }}
          sx={getDayCalendarSx(CALENDAR_COLUMN_GAP)}
        />
      </LocalizationProvider>

      {!hideUnavailableLegend && <UnavailableLegend />}

      <Divider sx={{ borderColor: (theme) => theme.palette.grey[100] }} />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.25,
            color: (theme) => theme.palette.grey[600],
          }}
        >
          {startDate && endDate
            ? t('days_selected', { count: endDate.diff(startDate, 'day') + 1 })
            : t('select_start_and_end_date')}
        </Typography>
        <Stack direction="row" alignItems="center" gap="12px">
          {extraAction}
          {!hideClear && (
            <AppButton
              variant={onApply ? 'secondary' : 'link'}
              size="small"
              onClick={() => onChange(null, null)}
              sx={
                onApply
                  ? { minHeight: '27px' }
                  : {
                      fontSize: '12px',
                      lineHeight: 1.25,
                      color: (theme) =>
                        startDate && endDate ? theme.palette.accent.main : theme.palette.grey[400],
                      paddingBottom: '2px !important',
                      borderRadius: 0,
                      borderBottom: (theme) =>
                        `1px solid ${startDate && endDate ? theme.palette.accent.main : theme.palette.grey[400]}`,
                      '&:hover': {
                        color: (theme) =>
                          startDate && endDate
                            ? theme.palette.accent.main
                            : theme.palette.grey[400],
                      },
                    }
              }
              data-testid={`${dataTestId}-clear`}
            >
              {t('clear')}
            </AppButton>
          )}
          {!!onApply && (
            <AppButton
              variant="primary"
              size="small"
              onClick={() => onApply(startDate, endDate)}
              sx={{ minHeight: '27px' }}
              data-testid={`${dataTestId}-apply`}
            >
              {t('apply')}
            </AppButton>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default CalendarDateRangePicker;
